/**
 * The client-side REVEAL engine — React-free, like the slider/video/parallax engines, so the storefront's
 * ONE global init script upgrades every SSR'd `[data-aqx-anim]` with zero framework JS. Nothing here runs
 * on the server (the module only DEFINES functions; the storefront calls it once, a React host calls it
 * from an effect).
 *
 * A section carrying `data-aqx-anim="rise"` starts displaced + transparent and settles into place the
 * first time it reaches the reader. That is the ONE primitive the reference pages are built on (Huawei's
 * `.txtani` → `.active`, repeated over all 21 of its sections): everything else is decoration on top.
 *
 * ## The contract (emitted by universalSectionProps)
 *
 *   [data-aqx-anim="fade|rise|slide-left|slide-right|zoom|blur"]   the animated element
 *     [data-aqx-anim-speed="slow|normal|fast"]   (optional) → `--aqx-anim-duration` in theme.css
 *     [data-aqx-anim-stagger]                    (optional) its `[data-aqx-anim-item]` children enter in turn
 *     [data-aqx-anim-item]                        a staggered child; the engine numbers it with `--aqx-anim-i`
 *   → the engine sets `data-aqx-anim-in` when it has arrived. All the actual animating is CSS.
 *
 * A section that takes the UNIVERSAL route carries no attribute at all: `animationVars` writes the same
 * thing as inline custom properties (`--aqx-anim-name`, `--aqx-anim-stagger`) because our 37 universal
 * sections pick `style` out of their shared props by hand and would drop a new attribute on the floor.
 * Both routes are collected here and both are styled by the same rules in theme.css.
 *
 * ## Why an "armed" flag, and why the arming happens in the <head>
 *
 * A reveal is the one animation that can LOSE CONTENT: its resting state is invisible. If the CSS hid
 * `[data-aqx-anim]` unconditionally and this module never ran — a bundle that 404s, a browser without
 * IntersectionObserver, a parse error three modules upstream — the shopper would get a blank page and the
 * seller would never know. So the hiding rules in theme.css are ALL scoped to
 * `html[data-aqx-anim-armed]`, and that attribute only ever appears when something is alive to take the
 * elements back out of hiding. No arming = every section paints exactly as it did before this engine
 * existed. (We have paid twice for the other assumption: the storefront hydrates ONE island, so 4 broken
 * thumbnails stayed broken on 2026-09-17, and a variant click before hydration was swallowed on
 * 2026-09-15. A reveal engine fails far louder than either.)
 *
 * The arming itself belongs in a blocking inline <head> snippet ({@link ARM_SNIPPET}), not here: this
 * module loads after first paint, so arming from here would paint the section visible, then hide it, then
 * animate it back — a flash on every section. The snippet also sets a dead-man's timer that DISARMS the
 * page if this engine never reports in, which is what turns "the bundle failed" from a blank page into a
 * plain one.
 */
/** The attribute the <head> snippet puts on `<html>`; every hiding rule in theme.css is scoped to it. */
export const ARMED_ATTR = 'data-aqx-anim-armed';
/** Set by the engine on an element that has arrived — the CSS animates on its presence. */
export const IN_ATTR = 'data-aqx-anim-in';
/**
 * Worn for two frames by an element being REPLAYED. theme.css kills every transition underneath it, so
 * the element can be dropped back to its resting state INSTANTLY instead of fading out to get there.
 */
export const RESET_ATTR = 'data-aqx-anim-reset';
/** The engine's liveness flag, read by the <head> snippet's dead-man's timer. */
export const READY_FLAG = '__aqxRevealReady';
import { choreograph, isUnveil, settleChoreo } from './revealChoreo.js';
/**
 * The inline `<head>` snippet, verbatim, for the storefront layout. Kept HERE, next to the engine that
 * answers it, so the two halves of the arming contract cannot drift apart in review.
 *
 * It arms the page before first paint, and disarms it again after 4s unless the engine has set its
 * liveness flag — so a bundle that never lands costs the animation, never the content.
 */
export const ARM_SNIPPET = `(function(){try{
if(matchMedia&&matchMedia('(prefers-reduced-motion: reduce)').matches)return;
if(typeof IntersectionObserver==='undefined')return;
var h=document.documentElement;h.setAttribute('${ARMED_ATTR}','');
setTimeout(function(){if(!window.${READY_FLAG})h.removeAttribute('${ARMED_ATTR}');},4000);
}catch(e){}})();`;
const noop = () => { };
function prefersReducedMotion() {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}
/**
 * Mark one element as arrived, numbering its staggered children on the way in.
 *
 * The children are named EXPLICITLY (`data-aqx-anim-item`), never guessed from the markup: our 62
 * sections have 62 different shapes, and an engine that picked "the direct children of the content frame"
 * would stagger a wrapper in some of them and the whole section in others — invisible in a unit test and
 * only findable by looking at all 62. A section opts its own repeatable items in, one line, where the
 * author can see what they are.
 */
function arrive(el) {
    if (el.hasAttribute('data-aqx-anim-stagger') ||
        (el.style.cssText ?? '').includes('--aqx-anim-stagger')) {
        const items = el.querySelectorAll('[data-aqx-anim-item]');
        items.forEach((item, i) => item.style.setProperty('--aqx-anim-i', String(i)));
    }
    el.setAttribute(IN_ATTR, '');
}
/**
 * Mark a BATCH of elements arrived at once. The unveil sections among them are choreographed together
 * first (one layout pass for all — see revealChoreo.ts), and the arrival flag goes on in the same task,
 * so the browser resolves "the block is shown" and "every unit inside starts hidden, on its own delay"
 * in ONE style pass. Two tasks would paint a frame with the section finished, and an entrance whose
 * first frame is the end is no entrance.
 *
 * `quiet` skips the choreography — for the reduced-motion / no-observer path, where nothing may move
 * and splitting a heading into lines it will never animate is pure DOM churn.
 */
function arriveAll(els, quiet = false) {
    if (!quiet) {
        const unveils = els.filter(isUnveil);
        if (unveils.length)
            choreograph(unveils);
    }
    for (const el of els)
        arrive(el);
}
/**
 * Wire every `[data-aqx-anim]` under `root` to ONE IntersectionObserver (not one per element: a long
 * product page carries 20+ animated sections, and 20 observers all firing on the same scroll is the kind
 * of cost that shows up on a mid-range phone, not on the founder's Mac).
 *
 * Returns a cleanup so a LIVE React host can re-bind without leaking the observer; the storefront's
 * static global init ignores the return.
 *
 * Under `prefers-reduced-motion` — or with no IntersectionObserver at all — every target is shown at once
 * and nothing is observed. Note that the page is not armed in either case (the <head> snippet checks the
 * same two conditions), so those elements were never hidden to begin with; marking them arrived is what
 * keeps a React host that armed by hand honest.
 */
export function initReveals(root) {
    const all = Array.from(root.querySelectorAll('[data-aqx-anim], [style*="--aqx-anim-name"]'));
    if (!all.length)
        return noop;
    /*
     * An element that has ALREADY arrived is done, and re-observing it is not merely wasted work — it
     * CANCELS a replay. `replayReveal` takes the arrival mark off and puts it back two frames later so the
     * transition re-runs; an observer bound in between fires on its very first frame and marks the element
     * arrived again before the browser has painted one hidden frame. The entrance then "plays" with
     * nothing to see.
     *
     * That is exactly what the builder canvas did on 2026-09-18 — and the e2e I had just written passed on
     * it, because it only asserted the END state (armed, arrived, items numbered, opacity back to 1). A
     * sampler reading the opacity on every frame is what caught it: 186 frames, not one below 1.
     */
    const targets = all.filter((el) => !el.hasAttribute(IN_ATTR));
    // Tell the <head> snippet's dead-man's timer that the animation half of the page is alive. Set even
    // when we go on to reveal everything immediately: the page stays armed, and the timer's job is to
    // catch a MISSING engine, not a deliberately quiet one.
    window[READY_FLAG] = true;
    if (typeof IntersectionObserver === 'undefined' || prefersReducedMotion()) {
        arriveAll(targets, true);
        return noop;
    }
    const io = new IntersectionObserver((entries) => {
        const arrived = [];
        for (const entry of entries) {
            if (!entry.isIntersecting)
                continue;
            arrived.push(entry.target);
            // Once only. The reference re-hides on the way back up; on a shop that reads as the page
            // twitching every time the buyer scrolls back to re-read a price.
            io.unobserve(entry.target);
        }
        if (arrived.length)
            arriveAll(arrived);
    }, 
    // The element must be 12% up from the bottom edge before it starts — entering ON the fold makes the
    // movement fight the scroll instead of following it.
    { rootMargin: '0px 0px -12% 0px', threshold: 0 });
    for (const el of targets)
        io.observe(el);
    return () => io.disconnect();
}
/**
 * Replay one element's entrance — remove the arrival mark, then set it again on the next frame so the
 * CSS transition actually re-runs (setting it back in the same frame is a no-op: the browser never sees
 * an intermediate state).
 *
 * This exists for the BUILDER: in the canvas nothing is armed, so a seller who picks an animation would
 * otherwise get no feedback at all and reasonably conclude the control is dead — a defect we have shipped
 * before ("2 interrupteurs morts", "bouton mort"). The canvas calls this when the setting changes.
 */
export function replayReveal(el) {
    /*
     * THE RESTING STATE HAS TO BE REACHED INSTANTLY, AND IT WAS NOT.
     *
     * Taking the arrival mark off does not snap the element back — the resting rule carries the SAME
     * `transition`, so the children begin a 600ms fade OUT towards it. Two frames later the mark went back
     * on and the fade reversed from wherever it had got to, which is about one percent of the way. The
     * replay therefore "ran" and moved nothing: measured in the builder canvas on 2026-09-18, the lowest
     * opacity across 117 sampled frames was 0.986. Nobody had noticed because nothing called this function
     * — it was written for the canvas and the canvas never used it.
     *
     * `RESET_ATTR` suppresses transitions underneath the element for exactly one painted frame: the
     * children snap to opacity 0, the browser paints that, and only then do the transitions come back and
     * the arrival mark with them.
     */
    el.setAttribute(RESET_ATTR, '');
    el.removeAttribute(IN_ATTR);
    // An unveil still in flight is taken apart NOW, not when its timer says so: the line boxes and the
    // per-unit delays belong to the entrance being abandoned, and the replay measures its own.
    settleChoreo(el);
    requestAnimationFrame(() => {
        // One frame has now been painted in the resting state, with no transition to soften it.
        el.removeAttribute(RESET_ATTR);
        requestAnimationFrame(() => arriveAll([el], prefersReducedMotion()));
    });
}
//# sourceMappingURL=revealInit.js.map